import React from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────────
// EnergyBreadcrumb — drill navigation.
//
// Two DIFFERENT affordances, deliberately:
//   • the Back arrow always returns to Level 1 (the acceptance criterion is
//     literal: "Back from Level 3 goes straight to Level 1"), and
//   • the crumb segments are individually clickable, which is the only way to
//     reach Level 2 again from Level 3.
//
// A DAY crumb (#385) sits between Overview and the pillar when the drill was
// entered from a bar of the daily chart: the levels below it are then scoped
// to that single day, and the crumb is how the user sees — and returns to —
// that scope.
// ─────────────────────────────────────────────────────────────────────────

const Crumb = ({ testId, label, onClick }) => {
    const className = 'text-[13px] truncate min-w-0 max-w-full';
    if (!onClick) {
        return (
            <span data-testid={testId} className={`${className} text-white font-semibold`}>
                {label}
            </span>
        );
    }
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`${className} text-pai-blue hover:underline transition-colors`}
        >
            {label}
        </button>
    );
};

const EnergyBreadcrumb = ({ level = 1, dayLabel, pillarLabel, categoryLabel, onOverview, onDay, onPillar }) => {
    const { t } = useLanguage();
    if (level <= 1 && !dayLabel) return null;

    return (
        <nav
            aria-label={t('energyBreadcrumb', 'Energy navigation')}
            className="flex items-center gap-1.5 flex-wrap min-w-0 max-w-full"
        >
            <button
                type="button"
                data-testid="energy-back"
                onClick={onOverview}
                aria-label={t('energyBack', 'Back to overview')}
                title={t('energyBack', 'Back to overview')}
                className="pai-icon-btn w-7 h-7 flex-shrink-0"
            >
                <ArrowLeft size={14} />
            </button>
            <Crumb testId="energy-crumb-overview" label={t('energyOverview', 'Overview')} onClick={onOverview} />
            {dayLabel && (
                <>
                    <ChevronRight size={12} className="text-pai-faint flex-shrink-0" />
                    <Crumb
                        testId="energy-crumb-day"
                        label={dayLabel}
                        // The day view itself → static; a way back from below it.
                        onClick={level >= 2 ? onDay : undefined}
                    />
                </>
            )}
            {pillarLabel && (
                <>
                    <ChevronRight size={12} className="text-pai-faint flex-shrink-0" />
                    <Crumb
                        testId="energy-crumb-pillar"
                        label={pillarLabel}
                        // Current page at Level 2 → static; a way back at Level 3.
                        onClick={level >= 3 ? onPillar : undefined}
                    />
                </>
            )}
            {level >= 3 && categoryLabel && (
                <>
                    <ChevronRight size={12} className="text-pai-faint flex-shrink-0" />
                    <Crumb testId="energy-crumb-category" label={categoryLabel} />
                </>
            )}
        </nav>
    );
};

export default EnergyBreadcrumb;
